export type { Article } from '@/modules/marketing/article-types';
import type { Article } from '@/modules/marketing/article-types';

import {
  commandRunner,
  playbooks,
  runHistory,
  safeRollouts,
  securityHardening,
  serverTemplates,
} from '@/modules/marketing/articles/automation';
import {
  apiMonitoring,
  dnsMonitoring,
  domainExpiryMonitoring,
  portMonitoring,
  sslMonitoring,
  websiteMonitoring,
} from '@/modules/marketing/articles/monitoring';
import {
  autoFix,
  backupVerification,
  backups,
  serverManagement,
} from '@/modules/marketing/articles/operations';
import { teamManagement } from '@/modules/marketing/articles/team-management';

/**
 * The article index.
 *
 * Each article lives in its own file with its own prose; this is only the list and the lookup. The
 * order here is the order they appear in the indexes and menus, so it should read as a sequence
 * somebody would actually follow rather than as alphabetical accident.
 *
 * ## One subject per article
 *
 * No article explains the platform. A reader who arrived from a search for "monitor ssl expiry"
 * wants that answer, and two of our own pages competing for the same query is worse than one page
 * ranking for it. Cross-references are links, never repeated sections.
 *
 * ## Articles for features that no longer exist are gone, not redirected
 *
 * The platform stopped deploying applications and managing domains, DNS, databases and scheduled
 * jobs. Their articles were removed rather than pointed at something else: a search result for "git
 * deployments" that lands on a page about playbooks is a bait-and-switch, and an honest 404 lets the
 * search engine drop the page. Firewalls and certificates returned as playbooks the customer runs,
 * and are written about only in that form.
 */
export const ARTICLES: Article[] = [
  // Automation, in the order somebody adopts it.
  playbooks,
  securityHardening,
  commandRunner,
  serverTemplates,
  safeRollouts,
  runHistory,

  // What keeps running afterwards.
  autoFix,
  backups,
  backupVerification,
  serverManagement,
  teamManagement,

  // Monitoring, from what most people set up first to what they forget entirely.
  websiteMonitoring,
  apiMonitoring,
  sslMonitoring,
  dnsMonitoring,
  domainExpiryMonitoring,
  portMonitoring,
];

export function articleBySlug(slug: string | undefined): Article | undefined {
  return ARTICLES.find((article) => article.slug === slug);
}
